import {MyTallyAgeQuery, TestResultCategory, TestResultLabSubmission} from '#apollo/schema';
import {TestResultListItemProps} from '#component/partial/TestResultListItem';

import {
  PendingKitHeader,
  PendingSurveyKitHeader,
  RegisterKitHeader,
  TallyAgeHeader,
} from './component';

/**
 * This function returns the rendered JSX to be provided as the `Hero`
 * component's children on the MyTallyAge page. There are three possible options
 * and associated conditions:
 *
 * 1. If the user has never registered a `labSubmission`, we return the
 *    rendered `RegisteredKitHeader`.
 * 2. If the user has registered a `labSubmission`, but has not yet recieved
 *    results, we return the rendered `PendingKitHeader`.
 * 3. If the user has recieved a `labAssessment`, we return the rendered
 *    `TallyAgeHeader`.
 */
export function getRenderedHeader({
  mostRecentLabSubmission,
  mostRecentLabSubmissionWithResults,
}: {
  mostRecentLabSubmission?: MyTallyAgeQuery['me']['mostRecentLabSubmission'];
  mostRecentLabSubmissionWithResults?: MyTallyAgeQuery['me']['mostRecentLabSubmissionWithResults'];
}) {
  if (!mostRecentLabSubmission) {
    return <RegisterKitHeader />;
  } else if (!mostRecentLabSubmission?.userSurveySubmission) {
    return <PendingSurveyKitHeader />;
  } else if (!mostRecentLabSubmissionWithResults?.labAssessment) {
    return <PendingKitHeader />;
  } else {
    const {labAssessment, registeredDate} = mostRecentLabSubmissionWithResults;

    return (
      <TallyAgeHeader
        deltaAge={labAssessment.deltaAge}
        formattedBioAge={labAssessment.formattedBioAge}
        formattedChronologicalAge={labAssessment.formattedChronologicalAge}
        formattedDeltaAge={labAssessment.formattedDeltaAge}
        labResultMetrics={labAssessment.labResultMetrics}
        labResultReportUrl={labAssessment.reportUrl}
        registeredDate={registeredDate}
      />
    );
  }
}

/**
 * Given an array of `category`s, this function will flatten each entry's
 * data structure for simpler prop-drilling (IE, reduce down to a bag of prop
 * values for the `TestResultListItem` component).
 */
export function getHistoryResultItemCategories(categories: TestResultCategory[] = []) {
  return categories.map(({category, id, categoryScore, maxCategoryScore}) => ({
    // Normalize categoryScore to percentage.
    categoryScore: Math.max(Math.min(Math.round((categoryScore / maxCategoryScore) * 100), 100), 0),
    id,
    maxCategoryScore: 100,
    title: category.title,
  }));
}

/**
 * Given an array of `labSubmissions`, this function will map over the array and
 * add a "count" property that estimates the given labSubmission as the nth
 * `labSubmission` a user has registered.
 *
 * This function assumes that the array will consist of _all_ of a users
 * `labSubmission`s, and that the array has been sorted reverse-chronologically.
 */
export function getCountedLabSubmissions(labSubmissions: TestResultLabSubmission[] = []) {
  return labSubmissions.map((labSubmission, i) => ({
    ...labSubmission,
    count: labSubmissions.length - i,
  }));
}

/**
 * Given an array of `labSubmission`s, this function will flatten each entry's
 * data structure for simpler prop-drilling (IE, reduce down to a bag of prop
 * values for the `TestResultListItem` component).
 */
export function getHistoryListItems(labSubmissions: TestResultLabSubmission[] = []) {
  const indexedLabSubmissions = getCountedLabSubmissions(labSubmissions);

  // Only include `labSubmissions` with assessments.
  const filteredLabSubmissions = indexedLabSubmissions.filter(({labAssessment}) => !!labAssessment);

  return filteredLabSubmissions.map(
    labSubmission =>
      ({
        categories: getHistoryResultItemCategories(
          labSubmission.userSurveySubmission?.surveyAssessment?.categories,
        ),
        count: labSubmission.count,
        deltaAge: labSubmission.labAssessment?.deltaAge,
        formattedBioAge: labSubmission.labAssessment?.formattedBioAge,
        formattedDeltaAge: labSubmission.labAssessment?.formattedDeltaAge,
        hasLabAssessment: !!labSubmission.labAssessment,
        id: labSubmission.id,
        lifestyleScore: labSubmission.userSurveySubmission?.surveyAssessment?.lifestyleScore,
        registeredDate: labSubmission.registeredDate,
      } as TestResultListItemProps),
  );
}
